/**
 * SqlConfidenceBadge — three-band confidence chip for SQL / structured-data
 * results.
 *
 * Pure presentational primitive (PR-A2). Reused by:
 *   1. ``<StructuredDataCard>``    — inline chat result (this PR)
 *   2. ``<EnterpriseDataPanel>``   — inline route panel (PR-A3)
 *   3. ``DashboardChartCard``      — Stream A chart header (PR-2 follow-up)
 *
 * Bands (matching the V1 ``confidence_score`` thresholds):
 *   ≥ 0.85       → "Affidabilità alta"    — emerald
 *   0.5 – 0.85   → "Affidabilità media"   — amber
 *   < 0.5        → "Affidabilità bassa"   — red
 *
 * Accessibility: ``aria-label`` reads ``"Affidabilità {alta|media|bassa}"``
 * so screen readers don't read the percentage twice (the visible label
 * already includes it).
 */

import type * as React from "react";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { cn } from "../lib/cn";

export type SqlConfidenceLevel = "alta" | "media" | "bassa";

export interface SqlConfidenceBadgeProps {
  /** Confidence score in the [0, 1] range. Clamped silently. */
  readonly confidence: number;
  /** Optional extra classes appended to the root <span>. */
  readonly className?: string;
}

/**
 * Public band classifier — exported so consumers (chart cards, panel
 * headers) that want band-driven chrome without the chip itself can stay
 * DRY.
 */
export function classifyConfidence(confidence: number): SqlConfidenceLevel {
  if (!Number.isFinite(confidence)) return "bassa";
  const clamped = Math.max(0, Math.min(1, confidence));
  if (clamped >= 0.85) return "alta";
  if (clamped >= 0.5) return "media";
  return "bassa";
}

const LEVEL_LABEL: Record<SqlConfidenceLevel, MessageDescriptor> = {
  alta: msg`Affidabilità alta`,
  media: msg`Affidabilità media`,
  bassa: msg`Affidabilità bassa`,
};

/**
 * Emerald / amber / red kept as explicit utility classes (no token
 * registry yet for chart-confidence colors). Aligned with the Ando palette
 * — emerald-500/amber-500/red-500 with low-alpha bg + ring border.
 */
const LEVEL_CLASSES: Record<SqlConfidenceLevel, string> = {
  alta: "bg-emerald-500/12 text-emerald-700 ring-emerald-500/30",
  media: "bg-amber-500/14 text-amber-700 ring-amber-500/30",
  bassa: "bg-red-500/12 text-red-700 ring-red-500/30",
};

export const SqlConfidenceBadge: React.FC<SqlConfidenceBadgeProps> = ({
  confidence,
  className,
}) => {
  const { i18n } = useLingui();
  const level = classifyConfidence(confidence);
  const clamped = Number.isFinite(confidence)
    ? Math.max(0, Math.min(1, confidence))
    : 0;
  const pct = Math.round(clamped * 100);
  const label = i18n._(LEVEL_LABEL[level]);

  return (
    <output
      aria-label={label}
      data-level={level}
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 ring-1",
        "font-medium text-[10px] uppercase tracking-[0.04em]",
        LEVEL_CLASSES[level],
        className,
      )}
    >
      <span aria-hidden>{label}</span>
      <span aria-hidden className="tabular-nums opacity-70">
        {pct}%
      </span>
    </output>
  );
};
SqlConfidenceBadge.displayName = "SqlConfidenceBadge";
